import { ref, computed } from 'vue'
import { usePrimeVue } from 'primevue/config'
import { setI18nLocale, SUPPORTED_LOCALES } from '../i18n'

const LOCALE_STORAGE_KEY = 'linkflare_locale'
const SIGNUP_LANG_KEY = 'linkflare_signup_lang'

// Global locale state — mirrors the useTheme.js module-scope pattern.
const currentLocale = ref('en')

// PrimeVue config is only reachable from a component with the app context;
// App.vue calls bindPrimeVue() once during setup so applyLocale can swap the
// component locale (datepicker, confirm buttons, …) at runtime.
let primevueConfig = null
let primeEnDefaults = null

/** 'de-AT' → 'de'; anything unsupported → null. */
export function normalizeLocale(raw) {
  if (!raw) return null
  const two = String(raw).trim().slice(0, 2).toLowerCase()
  return SUPPORTED_LOCALES.includes(two) ? two : null
}

/**
 * Pure resolution chain (unit-tested): the backend value wins once set;
 * NULL from the backend means "never chosen" so the pre-auth chain applies.
 * Order: backend → localStorage → signup capture → ?lang → lf_locale cookie
 * (set by the German marketing site on .linkflare.io) → browser → 'en'.
 */
export function resolveInitialLocale({
  backendLanguage,
  storedLocale,
  signupLang,
  queryLang,
  cookieLocale,
  navigatorLanguages
} = {}) {
  return normalizeLocale(backendLanguage)
    || normalizeLocale(storedLocale)
    || normalizeLocale(signupLang)
    || normalizeLocale(queryLang)
    || normalizeLocale(cookieLocale)
    || (navigatorLanguages || []).map(normalizeLocale).find(Boolean)
    || 'en'
}

export function readCookie(name, cookieString) {
  const source = cookieString !== undefined
    ? cookieString
    : (typeof document !== 'undefined' ? document.cookie : '')
  if (!source) return null
  for (const part of source.split(';')) {
    const eq = part.indexOf('=')
    if (eq === -1) continue
    if (part.slice(0, eq).trim() === name) {
      return decodeURIComponent(part.slice(eq + 1).trim())
    }
  }
  return null
}

function safeGet(storage, key) {
  try {
    return storage.getItem(key)
  } catch (e) {
    return null
  }
}

// Per-locale PrimeVue locale loaders (vendored from primefaces/primelocale).
// Each entry references a real file, so a locale only appears here once its
// prime-<loc>.js has been added — English uses PrimeVue's built-in defaults
// (snapshotted in bindPrimeVue) and needs no entry. Add a line per activated
// locale (must mirror SUPPORTED_LOCALES + the catalog loaders in i18n/index.js).
const PRIME_LOCALE_LOADERS = {
  de: () => import('../locales/prime-de.js').then((m) => m.primeDe),
  fr: () => import('../locales/prime-fr.js').then((m) => m.primeFr),
  es: () => import('../locales/prime-es.js').then((m) => m.primeEs),
  pt: () => import('../locales/prime-pt.js').then((m) => m.primePt),
  it: () => import('../locales/prime-it.js').then((m) => m.primeIt)
}

// Swap the PrimeVue component locale (datepicker, ConfirmDialog, paginator…).
// Extracted so bindPrimeVue() can apply it at mount: at BOOT primevueConfig is
// still null (bootstrapLocale runs before App.vue setup), so the boot locale's
// PrimeVue strings would otherwise never be applied for a same-locale session.
async function applyPrimeVueLocale(normalized) {
  if (!primevueConfig) return
  const loader = PRIME_LOCALE_LOADERS[normalized]
  if (loader) {
    primevueConfig.locale = await loader() // reactive in PrimeVue 4
  } else if (primeEnDefaults) {
    primevueConfig.locale = primeEnDefaults
  }
}

async function applyLocale(locale) {
  const normalized = normalizeLocale(locale) || 'en'
  await setI18nLocale(normalized)
  await applyPrimeVueLocale(normalized)
  if (typeof document !== 'undefined') {
    document.documentElement.lang = normalized
  }
  currentLocale.value = normalized
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, normalized)
  } catch (e) {
    // localStorage not available (e.g. private browsing with strict settings)
  }
}

/**
 * Called by main.js BEFORE mount: resolves the pre-auth chain (no backendUser
 * yet), lazy-loads the catalog and sets <html lang>. Returns the boot locale
 * so main.js can pick the matching Clerk localization.
 */
export async function bootstrapLocale() {
  let queryLang = null
  try {
    queryLang = new URLSearchParams(window.location.search).get('lang')
  } catch (e) { /* no window in non-browser contexts */ }
  const locale = resolveInitialLocale({
    storedLocale: safeGet(localStorage, LOCALE_STORAGE_KEY),
    signupLang: safeGet(sessionStorage, SIGNUP_LANG_KEY),
    queryLang,
    cookieLocale: readCookie('lf_locale'),
    navigatorLanguages: typeof navigator !== 'undefined'
      ? (navigator.languages || [navigator.language])
      : []
  })
  try {
    await applyLocale(locale)
    return locale
  } catch (e) {
    // A locale chunk (de.json / prime-de.js) failed to load — degrade to
    // English rather than rejecting bootstrap() and leaving the app unmounted.
    // The 'en' path does no dynamic import, so it can't fail the same way.
    console.warn('Locale bootstrap failed, falling back to English:', e)
    try { await applyLocale('en') } catch (e2) { /* last resort: ship defaults */ }
    return 'en'
  }
}

/**
 * SignUp.vue onMounted — mirrors the signup_referrer capture. The marker in
 * sessionStorage survives Clerk's same-tab OAuth redirects and tells the
 * post-auth sync "this session went through /sign-up", which is the ONLY
 * trigger for the one-time PUT (existing users signing in are never stamped).
 */
export function captureSignupLanguage() {
  let queryLang = null
  try {
    queryLang = new URLSearchParams(window.location.search).get('lang')
  } catch (e) { /* ignore */ }
  const captured = normalizeLocale(queryLang)
    || normalizeLocale(readCookie('lf_locale'))
    || (typeof navigator !== 'undefined'
      ? (navigator.languages || [navigator.language]).map(normalizeLocale).find(Boolean)
      : null)
  if (captured) {
    try {
      sessionStorage.setItem(SIGNUP_LANG_KEY, captured)
    } catch (e) { /* ignore */ }
  }
}

/** Returns the signup-captured locale once, then clears the marker. */
export function consumeSignupLanguage() {
  try {
    const value = sessionStorage.getItem(SIGNUP_LANG_KEY)
    if (value !== null) sessionStorage.removeItem(SIGNUP_LANG_KEY)
    return normalizeLocale(value)
  } catch (e) {
    return null
  }
}

/** Read the signup-captured locale WITHOUT clearing it (so a failed one-shot
 *  PUT can retry on the next load — clear only on success via clearSignupLanguage). */
export function peekSignupLanguage() {
  try {
    return normalizeLocale(sessionStorage.getItem(SIGNUP_LANG_KEY))
  } catch (e) {
    return null
  }
}

export function clearSignupLanguage() {
  try {
    sessionStorage.removeItem(SIGNUP_LANG_KEY)
  } catch (e) { /* ignore */ }
}

/** Non-reactive read for non-component modules (api.js Accept-Language). */
export function getCurrentLocale() {
  return currentLocale.value
}

export function useLocale() {
  // Call from App.vue setup (needs the app inject context).
  const bindPrimeVue = () => {
    primevueConfig = usePrimeVue().config
    // Snapshot the English baseline FIRST: boot's applyLocale ran before mount
    // when primevueConfig was null, so config.locale is still PrimeVue's built-in
    // English here regardless of the resolved app locale — exactly the baseline
    // we want for switch-back.
    if (primevueConfig && primevueConfig.locale && !primeEnDefaults) {
      primeEnDefaults = { ...primevueConfig.locale }
    }
    // Now apply the boot locale's PrimeVue strings (skipped at boot). Fire-and-
    // forget: a prime-de.js load failure leaves the (English) defaults in place.
    applyPrimeVueLocale(currentLocale.value).catch(() => {})
  }

  // UI-only switch; the PUT /users/language lives with the caller (token context).
  const setLocale = (locale) => applyLocale(locale)

  return {
    currentLocale: computed(() => currentLocale.value),
    setLocale,
    bindPrimeVue
  }
}
